import React from 'react';
import { CalculatorIcon } from '@heroicons/react/24/outline';

interface Props {
  onCreateQuote: () => void;
  /** P13 #34: opens the custom-filter builder. */
  onCustomFilter?: () => void;
  /** P13 #34: opens the multi-value custom-search box. */
  onCustomSearch?: () => void;
  /** Highlight the Custom Filter button when a custom filter is applied. */
  customFilterActive?: boolean;
  /** Highlight the Custom Search button when a custom search is applied. */
  customSearchActive?: boolean;
  /** Opens the standalone Rate Calculator. Omitted → the button isn't rendered. */
  onRateCalculator?: () => void;
}

const QuotesPageHeader: React.FC<Props> = ({
  onCreateQuote, onCustomFilter, onCustomSearch, customFilterActive, customSearchActive,
  onRateCalculator,
}) => {
  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-nowrap px-[22px] pt-3.5 pb-2.5 border-b border-slate-200 dark:border-slate-700">
        <h1 className="m-0 text-[1.0625rem] font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2.5 tracking-[-0.01em] whitespace-nowrap min-w-0 flex-shrink">
          {/* The match count lives in the TanTable header below — no badge here. */}
          <span className="text-blue-600 font-semibold">Quotes</span>
        </h1>
        <div className="flex items-center gap-4">
          {/* Prices a lane without creating (and saving) a throwaway quote. */}
          {onRateCalculator && (
            <button
              type="button"
              onClick={onRateCalculator}
              title="Calculate a drayage rate without creating a quote"
              className="px-3 py-[7px] text-[0.78125rem] font-medium border rounded-md cursor-pointer inline-flex items-center gap-1.5 bg-white text-slate-700 border-slate-300 hover:bg-slate-50 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600"
            >
              <CalculatorIcon className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500" />
              Rate Calculator
            </button>
          )}
          <button
            type="button"
            onClick={onCustomSearch}
            aria-pressed={!!customSearchActive}
            title={customSearchActive ? 'Custom search applied' : 'Custom Search'}
            className={`px-3 py-[7px] text-[0.78125rem] font-medium border rounded-md cursor-pointer inline-flex items-center gap-1.5 ${
              customSearchActive
                ? 'bg-indigo-600 text-white border-indigo-700 hover:bg-indigo-700 ring-2 ring-indigo-300 dark:border-indigo-500 dark:ring-indigo-700'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600'
            }`}
          >
            <span className={`text-[0.625rem] ${customSearchActive ? 'text-indigo-200' : 'text-slate-400 dark:text-slate-500'}`}>[m]</span>
            Custom Search
            {customSearchActive && <span className="w-1.5 h-1.5 rounded-full bg-white/90" aria-hidden="true" />}
          </button>
          <button
            type="button"
            onClick={onCustomFilter}
            aria-pressed={!!customFilterActive}
            title={customFilterActive ? 'Custom filter applied' : 'Custom Filter'}
            className={`px-3 py-[7px] text-[0.78125rem] font-medium border rounded-md cursor-pointer inline-flex items-center gap-1.5 ${
              customFilterActive
                ? 'bg-indigo-600 text-white border-indigo-700 hover:bg-indigo-700 ring-2 ring-indigo-300 dark:border-indigo-500 dark:ring-indigo-700'
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600'
            }`}
          >
            <span className={`text-[0.625rem] ${customFilterActive ? 'text-indigo-200' : 'text-slate-400 dark:text-slate-500'}`}>[n]</span>
            Custom Filter
            {customFilterActive && <span className="w-1.5 h-1.5 rounded-full bg-white/90" aria-hidden="true" />}
          </button>
          <button
            type="button"
            onClick={onCreateQuote}
            className="px-3.5 py-2 text-[0.78125rem] font-semibold bg-blue-600 text-white border border-blue-700 rounded-md cursor-pointer inline-flex items-center gap-1.5 shadow-sm hover:bg-blue-700"
          >
            <span className="opacity-75 text-[0.625rem]">[k]</span>
            + Create New Quote
          </button>
        </div>
      </div>

    </div>
  );
};

export default QuotesPageHeader;
